import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formSubmissionSchema } from '@qdf/shared';
import type { FormSubmissionInput, FormSubmissionResponse } from '@qdf/shared';
import { usePartnerPrefill } from '../../hooks/usePartnerPrefill.js';
import { useFormSubmission } from '../../hooks/useFormSubmission.js';
import { CompanyInfoSection } from './CompanyInfoSection.js';
import { CreditRequestSection } from './CreditRequestSection.js';
import { ConsentSection } from './ConsentSection.js';

interface IntakeFormProps {
  onSuccess: (response: FormSubmissionResponse) => void;
}

export function IntakeForm({ onSuccess }: IntakeFormProps) {
  const prefill = usePartnerPrefill();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormSubmissionInput>({
    resolver: zodResolver(formSubmissionSchema),
    defaultValues: {
      consentGiven: false as unknown as true,
      partnerSubmissionId: '',
      fullName: '',
      entityName: '',
      dba: '',
      email: '',
      phone: '',
      mailingAddress1: '',
      mailingAddress2: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: '',
      locAmountRequested: undefined as unknown as number,
      marketsOperating: '',
      avgAnnualTransactions: undefined as unknown as number,
      avgPurchasePrice: undefined as unknown as number,
      preferredTitleCompany: '',
      ...prefill,
    },
  });

  const partnerSubId = watch('partnerSubmissionId');

  const { submit, isSubmitting, error: submitError, fieldErrors } = useFormSubmission();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const response = await submit(data, []);
      onSuccess(response);
    } catch {
      // Error state is handled by useFormSubmission hook
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-8" noValidate>
      {/* Hidden partner submission ID (read-only, pre-filled via URL) */}
      <input type="hidden" {...register('partnerSubmissionId')} />
      {partnerSubId ? (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <span className="text-indigo-600 text-sm font-semibold">Partner Reference:</span>
          <span className="text-indigo-900 text-sm font-mono">{partnerSubId}</span>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-300 rounded-lg px-4 py-3">
          <p className="text-red-700 text-sm font-semibold">⚠️ Missing Partner Reference</p>
          <p className="text-red-600 text-sm mt-1">
            This form must be accessed through the partner portal link. Please contact your representative for the correct URL.
          </p>
        </div>
      )}

      <CompanyInfoSection register={register} errors={errors} />
      <CreditRequestSection register={register} errors={errors} />
      <ConsentSection register={register} errors={errors} />

      {submitError && (
        <div className="bg-error-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-error-500">{submitError}</p>
          {fieldErrors && (
            <ul className="mt-2 text-xs text-error-500 list-disc list-inside">
              {Object.entries(fieldErrors).map(([field, msgs]) =>
                msgs.map((msg) => (
                  <li key={`${field}-${msg}`}>
                    {field}: {msg}
                  </li>
                )),
              )}
            </ul>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`
            px-8 py-3 rounded-lg font-semibold text-white text-sm transition-all uppercase tracking-wider
            ${isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-accent-500 hover:bg-accent-600 active:bg-brand-700 shadow-sm hover:shadow'
            }
          `}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </div>
    </form>
  );
}
